/**
 * PlanPartitions.jsx — v2.20 extracted from PlanView.jsx
 *
 * Renders interior partitions inside the plan view's clipPath group:
 *   • Each partition as a thick wall segmented around doors + windows
 *   • Selection halo + draggable endpoints + ✕-delete button when selected
 *   • Rubber-band preview while drawing (with straight-line snap unless Shift)
 *
 * All shared geometry / state is passed in via props so this module is
 * pure-presentation — no useState, no direct project mutations.
 */
import React from "react";

export default function PlanPartitions({
  project,
  scale,
  toSvg,
  fmtLenShort,
  selection,
  setSelection,
  partitionMode,
  trayMode,
  equipmentMode,
  onPartitionDelete,
  partEndDrag,
  setPartEndDrag,
  pendingPartition,
  partCursor,
}) {
  return (
    <>
      {/* v1.84 — Interior partitions wrapped in a clipPath group so partitions
          cannot visually extend past the room boundary even if the underlying
          data has stale out-of-bounds coords. v2.20 — extracted from PlanView. */}
      <g clipPath="url(#planRoomClip)" data-testid="partitions-layer">
        {(project.room_partitions || []).map((part, i) => {
          const A = toSvg(part.x1, part.y1);
          const B = toSvg(part.x2, part.y2);
          const dx = B.x - A.x, dy = B.y - A.y;
          const len = Math.hypot(dx, dy) || 1;
          const ux = dx / len, uy = dy / len;
          const isSelected = selection?.kind === "partition" && selection.id === part.id;
          // v2.13 — read doors as a unified array; migrate legacy single-door.
          const doorsArr = Array.isArray(part.doors) && part.doors.length
            ? part.doors
            : (part.door ? [part.door] : []);
          // Build the gap list — one entry per door + one per window.
          const gaps = doorsArr.map((d, di) => {
            const off = Math.max(0, Math.min(1, d.offset ?? 0.5));
            const dPx = (d.width_in || 36) * scale;
            const c   = len * off;
            return {
              kind: "door",
              id: d.id || `door-${di}`,
              start: Math.max(0, c - dPx / 2),
              end:   Math.min(len, c + dPx / 2),
            };
          });
          const lenIn = len / scale;
          (part.windows || []).forEach((w) => {
            const off = Math.max(0, Math.min(1, w.offset ?? 0.5));
            const wPx = (w.width_in || 36) * scale;
            const c = len * off;
            gaps.push({
              kind: "window",
              start: Math.max(0, c - wPx / 2),
              end:   Math.min(len, c + wPx / 2),
              id: w.id,
            });
          });
          gaps.sort((a, b) => a.start - b.start);
          // Build solid-wall segments between adjacent gaps.
          const segs = [];
          let cursor = 0;
          gaps.forEach((g) => {
            if (g.start > cursor + 0.5) segs.push({ from: cursor, to: g.start });
            cursor = Math.max(cursor, g.end);
          });
          if (cursor < len - 0.5) segs.push({ from: cursor, to: len });
          const onClickPart = (ev) => {
            ev.stopPropagation();
            if (partitionMode || trayMode || equipmentMode) return;
            setSelection && setSelection({ kind: "partition", id: part.id });
          };
          const onContextDelete = (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (onPartitionDelete) {
              if (window.confirm(`Delete partition SEG-${String(i + 1).padStart(2, "0")}?`)) {
                onPartitionDelete(part.id);
                if (isSelected) setSelection && setSelection({ kind: null, id: null });
              }
            }
          };
          const startEndDrag = (endpoint) => (ev) => {
            ev.stopPropagation();
            if (ev.preventDefault) ev.preventDefault();
            if (!isSelected) setSelection && setSelection({ kind: "partition", id: part.id });
            setPartEndDrag({ partitionId: part.id, endpoint });
          };
          const draggingA = partEndDrag?.partitionId === part.id && partEndDrag?.endpoint === "A";
          const draggingB = partEndDrag?.partitionId === part.id && partEndDrag?.endpoint === "B";
          const haloStroke = isSelected ? "#FFCC00" : "transparent";
          const midX = (A.x + B.x) / 2;
          const midY = (A.y + B.y) / 2;
          const px = -uy, py = ux;
          const doorCount = doorsArr.length;
          const winCount  = (part.windows || []).length;
          return (
            <g key={part.id || `partition-${i}`} data-testid={`partition-${part.id || i}`}
               onClick={onClickPart} onContextMenu={onContextDelete}
               style={{ cursor: (partitionMode || trayMode || equipmentMode) ? undefined : "pointer" }}>
              {/* invisible wide hit area — see v2.13 note in PlanView. */}
              <line x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                    stroke="transparent" strokeWidth="20" strokeLinecap="round"
                    pointerEvents="stroke" />
              <line x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                    stroke={haloStroke} strokeOpacity={0.5}
                    strokeWidth="14" strokeLinecap="round" pointerEvents="none" />
              {segs.map((s, k) => {
                const sx = A.x + ux * s.from, sy = A.y + uy * s.from;
                const ex = A.x + ux * s.to,   ey = A.y + uy * s.to;
                return (
                  <g key={`seg-${k}`}>
                    <line x1={sx} y1={sy} x2={ex} y2={ey}
                          stroke="#8B8F97" strokeWidth="6" strokeLinecap="round" />
                    <line x1={sx} y1={sy} x2={ex} y2={ey}
                          stroke="#1A1F29" strokeWidth="2" strokeLinecap="round" />
                  </g>
                );
              })}
              {gaps.map((g, k) => {
                const sx = A.x + ux * g.start, sy = A.y + uy * g.start;
                const ex = A.x + ux * g.end,   ey = A.y + uy * g.end;
                const color = g.kind === "door" ? "#FF9F0A" : "#00E5FF";
                return (
                  <line key={`gap-${k}`} x1={sx} y1={sy} x2={ex} y2={ey}
                        stroke={color} strokeWidth={g.kind === "window" ? 3 : 2}
                        strokeDasharray={g.kind === "window" ? "5 3" : "3 2"}
                        strokeLinecap="round" />
                );
              })}
              <circle cx={A.x} cy={A.y} r="3" fill="#1A1F29" stroke="#8B8F97" strokeWidth="1.2" />
              <circle cx={B.x} cy={B.y} r="3" fill="#1A1F29" stroke="#8B8F97" strokeWidth="1.2" />
              <text x={midX} y={midY - 8}
                    fill={isSelected ? "#FFCC00" : "#FF9F0A"} fontFamily="JetBrains Mono" fontSize="9"
                    textAnchor="middle" pointerEvents="none">
                {`SEG-${String(i + 1).padStart(2, "0")} · ${fmtLenShort(lenIn, project)} · ${doorCount} door${doorCount === 1 ? "" : "s"}${winCount ? ` · ${winCount} win` : ""}`}
              </text>
              {isSelected && (
                <g data-testid={`partition-${part.id}-handles`}>
                  {[
                    { p: A, key: "A", dragging: draggingA },
                    { p: B, key: "B", dragging: draggingB },
                  ].map(({ p, key, dragging }) => (
                    <g key={`pe-${key}`}
                       onMouseDown={startEndDrag(key)}
                       onTouchStart={startEndDrag(key)}
                       style={{ cursor: "grab", touchAction: "none" }}
                       data-testid={`partition-${part.id}-end-${key}`}>
                      <circle cx={p.x} cy={p.y} r={dragging ? 8 : 6}
                              fill={dragging ? "#FFCC00" : "#FF9F0A"}
                              stroke="#0A0C10" strokeWidth="1.4" />
                      <text x={p.x} y={p.y + 3} fontFamily="JetBrains Mono"
                            fontSize="8" fontWeight="800" fill="#0A0C10"
                            textAnchor="middle" pointerEvents="none">{key}</text>
                    </g>
                  ))}
                  <g onClick={(ev) => { ev.stopPropagation(); onContextDelete(ev); }}
                     style={{ cursor: "pointer" }}
                     data-testid={`partition-${part.id}-delete`}>
                    <circle cx={midX + px * 16} cy={midY + py * 16} r="9"
                            fill="#FF4444" stroke="#0A0C10" strokeWidth="1.4" />
                    <text x={midX + px * 16} y={midY + py * 16 + 4}
                          fontFamily="JetBrains Mono" fontSize="12" fontWeight="800"
                          fill="#FFFFFF" textAnchor="middle" pointerEvents="none">×</text>
                  </g>
                  {(draggingA || draggingB) && (() => {
                    const ep = draggingA ? { x: part.x1, y: part.y1 } : { x: part.x2, y: part.y2 };
                    const epSvg = draggingA ? A : B;
                    return (
                      <text x={epSvg.x + 10} y={epSvg.y - 10}
                            fill="#FFCC00" fontFamily="JetBrains Mono"
                            fontSize="10" fontWeight="700" pointerEvents="none">
                        {`SEG-${String(i + 1).padStart(2, "0")} · ${draggingA ? "A" : "B"} ${ep.x},${ep.y}″`}
                      </text>
                    );
                  })()}
                </g>
              )}
            </g>
          );
        })}
      </g>

      {/* In-progress partition: rubber-band from first click to cursor */}
      {pendingPartition && (() => {
        const A = toSvg(pendingPartition.x, pendingPartition.y);
        let cx = partCursor?.x ?? pendingPartition.x;
        let cy = partCursor?.y ?? pendingPartition.y;
        if (partCursor && !partCursor.shift) {
          const ddx = Math.abs(cx - pendingPartition.x);
          const ddy = Math.abs(cy - pendingPartition.y);
          if (ddx >= ddy) cy = pendingPartition.y;
          else            cx = pendingPartition.x;
        }
        const B = toSvg(cx, cy);
        const previewLen = Math.hypot(cx - pendingPartition.x, cy - pendingPartition.y);
        return (
          <g data-testid="partition-pending" pointerEvents="none">
            <line x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                  stroke="#FF9F0A" strokeWidth="3" strokeDasharray="6 4" strokeLinecap="round" />
            <circle cx={A.x} cy={A.y} r="6" fill="#FF9F0A" stroke="#FFFFFF" strokeWidth="1.5" />
            <circle cx={B.x} cy={B.y} r="5" fill="#FFCC00" stroke="#0A0C10" strokeWidth="1.2" />
            {previewLen > 6 && (
              <text x={(A.x + B.x) / 2} y={(A.y + B.y) / 2 - 8}
                    fill="#FFCC00" fontFamily="JetBrains Mono" fontSize="10"
                    textAnchor="middle" fontWeight="700">
                {fmtLenShort(previewLen, project)} · {partCursor?.shift ? "free (shift)" : "straight"}
              </text>
            )}
          </g>
        );
      })()}
    </>
  );
}
